import { useMemo, useState, useEffect } from 'react';
import { Alert, Card, Col, Form, Row } from 'react-bootstrap';
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, ComposedChart, LabelList, Legend, Line,
  ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis
} from 'recharts';
import InfoTip from './InfoTip';

// v3_fix_issues #7 & #8 (owner side) - the rental owner's own business insights: the scoped-down
// mirror of the website owner's Business tab. Everything is computed CLIENT-SIDE from the
// bookings the dashboard already loaded (getMyBookings returns the owner view, already limited to
// this owner's own listings) - no new API call, and nothing here can leak another owner's data.
//
// "Earnings" on this screen = RentalBooking.ownerEarnings (the owner's take-home). The website's
// cut (platformCommission) is never shown as the owner's earnings. Cancelled bookings earn
// nothing, so they're excluded from every money figure (still counted under bookings/cancelled).
// Basis date = booking createdAt ("when the business came in").

const PALETTE = {
  light: { earnings: '#2a78d6', bookings: '#eb6834', confirmed: '#1baf7a', grid: '#e6e6e3', axis: '#52514e' },
  dark: { earnings: '#3987e5', bookings: '#d95926', confirmed: '#199e70', grid: '#3a3a38', axis: '#c3c2b7' }
};

function usePalette() {
  const [dark, setDark] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setDark(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return dark ? PALETTE.dark : PALETTE.light;
}

const RANGE_OPTIONS = [
  { key: 'this_month', label: 'This month', grain: 'week' },
  { key: 'last_month', label: 'Last month', grain: 'week' },
  { key: 'last_3_months', label: 'Last 3 months', grain: 'month' },
  { key: 'last_6_months', label: 'Last 6 months', grain: 'month' },
  { key: 'this_year', label: 'This financial year', grain: 'month' },
  { key: 'all', label: 'All time', grain: 'month' }
];

const money = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const moneyShort = (n) => {
  const v = Math.abs(n || 0);
  if (v >= 1e7) return `₹${(n / 1e7).toFixed(1)}Cr`;
  if (v >= 1e5) return `₹${(n / 1e5).toFixed(1)}L`;
  if (v >= 1e3) return `₹${(n / 1e3).toFixed(1)}k`;
  return `₹${Math.round(n || 0)}`;
};

function fiscalYearStart(now, offset = 0) {
  const y = (now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1) + offset;
  return new Date(y, 3, 1);
}

// [start, end) for a range key, or null for "all time". `now` injectable for tests.
function rangeBounds(key, now = new Date()) {
  const m = now.getMonth();
  const y = now.getFullYear();
  if (key === 'this_month') return [new Date(y, m, 1), new Date(y, m + 1, 1)];
  if (key === 'last_month') return [new Date(y, m - 1, 1), new Date(y, m, 1)];
  if (key === 'last_3_months') return [new Date(y, m - 2, 1), new Date(y, m + 1, 1)];
  if (key === 'last_6_months') return [new Date(y, m - 5, 1), new Date(y, m + 1, 1)];
  if (key === 'this_year') { const s = fiscalYearStart(now); return [s, new Date(s.getFullYear() + 1, 3, 1)]; }
  return null;
}

function previousBounds(bounds) {
  if (!bounds) return null;
  const [start, end] = bounds;
  const span = end.getTime() - start.getTime();
  return [new Date(start.getTime() - span), new Date(start.getTime())];
}

function bucketStart(date, grain) {
  const d = new Date(date);
  if (grain === 'month') return new Date(d.getFullYear(), d.getMonth(), 1);
  const day = d.getDay();
  const diffToMonday = (day === 0 ? -6 : 1) - day;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + diffToMonday);
}
function nextBucket(date, grain) {
  const s = bucketStart(date, grain);
  return grain === 'month' ? new Date(s.getFullYear(), s.getMonth() + 1, 1) : new Date(s.getTime() + 7 * 86400000);
}
function fmtBucket(iso, grain) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return grain === 'month'
    ? d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' })
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

const inRange = (d, bounds) => {
  if (!bounds) return true;
  const t = new Date(d).getTime();
  return t >= bounds[0].getTime() && t < bounds[1].getTime();
};
const notCancelled = (b) => b.status !== 'cancelled';

function summarise(rows) {
  const earning = rows.filter(notCancelled);
  const earnings = earning.reduce((s, b) => s + (b.ownerEarnings || 0), 0);
  const grossRentalValue = earning.reduce((s, b) => s + (b.rentalAmount || 0), 0);
  const pendingPayout = rows
    .filter((b) => ['PENDING', 'PROCESSING'].includes(b.payoutStatus))
    .reduce((s, b) => s + (b.ownerEarnings || 0), 0);
  const customerCounts = rows.reduce((acc, b) => {
    const key = (b.email || b.customerName || '').toLowerCase();
    if (key) acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  return {
    earnings,
    grossRentalValue,
    bookings: rows.length,
    confirmed: rows.filter((b) => b.status === 'confirmed').length,
    cancelled: rows.filter((b) => b.status === 'cancelled').length,
    pendingPayout,
    avgEarning: earning.length ? earnings / earning.length : 0,
    uniqueCustomers: Object.keys(customerCounts).length,
    repeatCustomers: Object.values(customerCounts).filter((n) => n > 1).length
  };
}

const KPIS = [
  { key: 'earnings', label: 'Your earnings', money: true, help: 'Your take-home (owner earnings) on non-cancelled bookings in this period.' },
  { key: 'pendingPayout', label: 'Payout owed to you', money: true, help: 'Earnings the platform has collected and still owes you (payout pending or processing).' },
  { key: 'bookings', label: 'Bookings', help: 'Bookings across your listings in this period, any status.' },
  { key: 'confirmed', label: 'Confirmed', help: 'Bookings you accepted.' },
  { key: 'cancelled', label: 'Cancellations', help: 'Bookings cancelled in this period.', invert: true },
  { key: 'grossRentalValue', label: 'Rental value', money: true, help: 'Total rental amount customers paid for your listings (before the platform split).' },
  { key: 'avgEarning', label: 'Avg. per booking', money: true, help: 'Your earnings divided by non-cancelled bookings.' },
  { key: 'repeatCustomers', label: 'Repeat customers', help: 'Customers who booked your listings more than once in this period.' }
];

function Delta({ current, previous, invert }) {
  if (previous == null) return <span className="text-muted small">no earlier data</span>;
  const abs = Math.round((current - previous) * 100) / 100;
  if (!abs) return <span className="text-muted small">no change</span>;
  const up = abs > 0;
  const good = invert ? !up : up;
  const pct = previous !== 0 ? Math.round(Math.abs(abs / previous) * 100) : null;
  return (
    <span className={`small ${good ? 'text-success' : 'text-danger'}`}>
      {up ? '▲' : '▼'} {Math.abs(abs).toLocaleString('en-IN')}{pct != null ? ` (${pct}%)` : ''}
    </span>
  );
}

function ChartCard({ title, subtitle, children, height = 240 }) {
  return (
    <Card className="mb-3">
      <Card.Body>
        <Card.Title as="h6" className="mb-0">{title}</Card.Title>
        {subtitle && <div className="text-muted small mb-2">{subtitle}</div>}
        <div style={{ width: '100%', height }}>
          <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
        </div>
      </Card.Body>
    </Card>
  );
}

export default function OwnerBusinessInsights({ bookings = [], items = [] }) {
  const pal = usePalette();
  const [range, setRange] = useState('last_6_months');

  const grain = RANGE_OPTIONS.find((r) => r.key === range)?.grain || 'month';
  const itemName = useMemo(() => {
    const m = new Map(items.map((i) => [String(i._id), i.name]));
    return (id) => m.get(String(id)) || 'Listing';
  }, [items]);

  const { current, previous, series, byListing, topCustomers, hasAny } = useMemo(() => {
    const bounds = rangeBounds(range);
    const prevB = previousBounds(bounds);
    const cur = bookings.filter((b) => inRange(b.createdAt, bounds));
    const prev = prevB ? bookings.filter((b) => inRange(b.createdAt, prevB)) : null;

    // time series over the current window (fall back to min/max createdAt for "all time")
    let start = bounds?.[0];
    let end = bounds?.[1];
    if (!start && cur.length) {
      const times = cur.map((b) => new Date(b.createdAt).getTime());
      start = new Date(Math.min(...times));
      end = new Date(Math.max(...times) + 86400000);
    }
    const buckets = [];
    if (start && end) {
      let cursor = bucketStart(start, grain);
      let guard = 0;
      while (cursor < end && guard++ < 400) {
        const bStart = cursor;
        const bEnd = nextBucket(cursor, grain);
        const rows = cur.filter((b) => {
          const t = new Date(b.createdAt).getTime();
          return t >= bStart.getTime() && t < bEnd.getTime();
        });
        const sum = summarise(rows);
        buckets.push({ bucket: bStart.toISOString(), earnings: Math.round(sum.earnings), bookings: sum.bookings, confirmed: sum.confirmed });
        cursor = bEnd;
      }
    }

    const listingAgg = {};
    const customerAgg = {};
    for (const b of cur.filter(notCancelled)) {
      const li = String(b.itemId);
      listingAgg[li] = (listingAgg[li] || 0) + (b.ownerEarnings || 0);
      const ck = (b.customerName || b.email || 'Customer');
      customerAgg[ck] = customerAgg[ck] || { label: ck, earnings: 0, bookings: 0 };
      customerAgg[ck].earnings += b.ownerEarnings || 0;
      customerAgg[ck].bookings += 1;
    }

    return {
      current: summarise(cur),
      previous: prev ? summarise(prev) : null,
      series: buckets,
      byListing: Object.entries(listingAgg)
        .map(([id, earnings]) => ({ label: itemName(id), earnings: Math.round(earnings) }))
        .sort((a, b) => b.earnings - a.earnings)
        .slice(0, 8),
      topCustomers: Object.values(customerAgg)
        .map((c) => ({ ...c, earnings: Math.round(c.earnings) }))
        .sort((a, b) => b.earnings - a.earnings)
        .slice(0, 8),
      hasAny: cur.length > 0
    };
  }, [bookings, range, grain, itemName]);

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-3">
        <Form.Label className="small mb-0">Period</Form.Label>
        <Form.Select
          size="sm" aria-label="Insights period" style={{ maxWidth: 220 }}
          value={range} onChange={(e) => setRange(e.target.value)}
        >
          {RANGE_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </Form.Select>
      </div>

      {!hasAny ? (
        <Alert variant="light">No bookings in this period yet.</Alert>
      ) : (
        <>
          {previous && <div className="text-muted small mb-2">Compared with the preceding period of equal length.</div>}
          <Row xs={2} sm={3} lg={4} className="g-2 mb-3">
            {KPIS.map((k) => (
              <Col key={k.key}>
                <Card className="h-100">
                  <Card.Body className="py-2 px-3">
                    <div className="text-muted small d-flex align-items-center">
                      {k.label}<InfoTip text={k.help} />
                    </div>
                    <div className="fs-5 fw-semibold">
                      {k.money ? money(current[k.key]) : (current[k.key] ?? 0).toLocaleString('en-IN')}
                    </div>
                    <Delta current={current[k.key]} previous={previous ? previous[k.key] : null} invert={k.invert} />
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>

          <Row>
            <Col lg={6}>
              <ChartCard title="Your earnings over time" subtitle="Owner earnings, by booking date">
                <AreaChart data={series} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke={pal.grid} vertical={false} />
                  <XAxis dataKey="bucket" tickFormatter={(v) => fmtBucket(v, grain)} tick={{ fill: pal.axis, fontSize: 11 }} />
                  <YAxis tickFormatter={moneyShort} tick={{ fill: pal.axis, fontSize: 11 }} width={56} />
                  <RTooltip formatter={(v) => money(v)} labelFormatter={(v) => fmtBucket(v, grain)} />
                  <Area type="monotone" dataKey="earnings" name="Your earnings" stroke={pal.earnings} strokeWidth={2} fill={pal.earnings} fillOpacity={0.15} />
                </AreaChart>
              </ChartCard>
            </Col>
            <Col lg={6}>
              <ChartCard title="Bookings over time" subtitle="All bookings vs. those you confirmed">
                <ComposedChart data={series} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke={pal.grid} vertical={false} />
                  <XAxis dataKey="bucket" tickFormatter={(v) => fmtBucket(v, grain)} tick={{ fill: pal.axis, fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fill: pal.axis, fontSize: 11 }} width={32} />
                  <RTooltip labelFormatter={(v) => fmtBucket(v, grain)} />
                  <Legend />
                  <Bar dataKey="bookings" name="All bookings" fill={pal.bookings} radius={[4, 4, 0, 0]} maxBarSize={26} />
                  <Line type="monotone" dataKey="confirmed" name="Confirmed" stroke={pal.confirmed} strokeWidth={2} dot={false} />
                </ComposedChart>
              </ChartCard>
            </Col>
          </Row>

          <Row>
            <Col lg={6}>
              <ChartCard title="Earnings by listing" subtitle="Your top-earning listings this period" height={220}>
                <BarChart data={byListing} layout="vertical" margin={{ top: 4, right: 48, bottom: 0, left: 8 }}>
                  <CartesianGrid stroke={pal.grid} horizontal={false} />
                  <XAxis type="number" tickFormatter={moneyShort} tick={{ fill: pal.axis, fontSize: 11 }} />
                  <YAxis type="category" dataKey="label" width={120} tick={{ fill: pal.axis, fontSize: 11 }} />
                  <RTooltip formatter={(v) => money(v)} />
                  <Bar dataKey="earnings" name="Your earnings" fill={pal.earnings} radius={[0, 4, 4, 0]} maxBarSize={20}>
                    <LabelList dataKey="earnings" position="right" formatter={moneyShort} style={{ fill: pal.axis, fontSize: 11 }} />
                  </Bar>
                </BarChart>
              </ChartCard>
            </Col>
            <Col lg={6}>
              <ChartCard title="Top customers" subtitle="By your earnings from them" height={220}>
                <BarChart data={topCustomers} layout="vertical" margin={{ top: 4, right: 48, bottom: 0, left: 8 }}>
                  <CartesianGrid stroke={pal.grid} horizontal={false} />
                  <XAxis type="number" tickFormatter={moneyShort} tick={{ fill: pal.axis, fontSize: 11 }} />
                  <YAxis type="category" dataKey="label" width={120} tick={{ fill: pal.axis, fontSize: 11 }} />
                  <RTooltip formatter={(v) => money(v)} />
                  <Bar dataKey="earnings" name="Your earnings" fill={pal.earnings} radius={[0, 4, 4, 0]} maxBarSize={20}>
                    <LabelList dataKey="earnings" position="right" formatter={moneyShort} style={{ fill: pal.axis, fontSize: 11 }} />
                  </Bar>
                </BarChart>
              </ChartCard>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}
